import isNil from 'lodash/isNil';
import { observable, action, makeObservable, runInAction } from 'mobx';
import { APIClient as ScaffoldClient, FetchUserResponse, SignOutUserResponse, AppError } from '@groma-dev/scaffold';
import { AppConfig } from '../AppConfig';
import { User, Rental, ShowingAvailability } from '../models';
import { APIClient } from '../apiclient/APIClient';
import {
  FetchRentalsRequest,
  FetchRentalDetailsRequest,
  FetchRentalDetailsResponse,
  FetchRentalsResponse,
  FetchShowingAvailabilityRequest,
  FetchShowingAvailabilityResponse,
  ScheduleShowingRequest,
  ScheduleShowingResponse,
  GetAgentRequest,
  GetAgentResponse,
  UpdateOrCreateAgentRequest,
  UpdateOrCreateAgentResponse,
  CreateLeadRequest,
  FetchLeadByUuidResponse,
  Lead,
} from '../apiclient/APIClientInterface';
import { APIResponse } from '../apiclient/BaseAPIClient';
import { rentalsMock, availableShowingTimesMock } from '../../web/__mocks__';
import { UserStateInterface, ShowingAvailabilityStateInterface, ScaffoldClientHeaders } from './DataStoreInterface';

const userInitialState: UserStateInterface = {
  fetchingUser: false,
  user: undefined,
};

export default class DataStore {
  private static instance: any = null;

  @observable
  userState = userInitialState;

  @observable
  rentalsState = {
    fetchingRentals: false,
    rentals: [] as Rental[],
  };

  @observable
  rentalState = {
    fetchingRental: false,
    rental: null as Rental | null,
  };

  @observable
  showingAvailabilityState: ShowingAvailabilityStateInterface = {
    fetchingAvailability: false,
    availability: [],
  };

  @observable
  leadState = {
    fetchingLead: false,
    lead: null as Lead | null,
  };

  constructor() {
    if (isNil(DataStore.instance)) {
      DataStore.instance = this;
    }

    makeObservable(this);
  }

  @action
  fetchUser = async (): Promise<FetchUserResponse> => {
    this.setFetchingUser(true);
    const { baseUrl } = AppConfig.Settings.Server;
    const response: FetchUserResponse = await ScaffoldClient.fetchUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
    });

    if (response.success) this.setUser(response.user);
    this.setFetchingUser(false);

    return response;
  };

  @action
  fetchRentals = async (request: FetchRentalsRequest): Promise<FetchRentalsResponse> => {
    runInAction(() => {
      this.rentalsState.fetchingRentals = true;
    });

    let response: FetchRentalsResponse;
    try {
      if (AppConfig.Settings.Server.useMockData) {
        response = {
          date: new Date(),
          request,
          status: 200,
          success: true,
          rentals: rentalsMock,
          nextPageUrl: '',
        };
      } else {
        response = await APIClient.fetchRentals(request);
      }
      if (response.success) {
        this.setRentals(response.rentals || []);
      }
    } catch (error) {
      console.error(error);
      response = {
        date: new Date(),
        request,
        success: false,
        rentals: [],
        status: 500,
      };
    } finally {
      runInAction(() => {
        this.rentalsState.fetchingRentals = false;
      });
    }

    return response;
  };

  @action
  fetchLeadByUuid = async (uuid?: string): Promise<FetchLeadByUuidResponse> => {
    if (!uuid) {
      this.leadState.lead = null;
      return {
        date: new Date(),
        request: { uuid: '' },
        status: 200,
        success: true,
        lead: null,
      } as FetchLeadByUuidResponse;
    }

    this.leadState.fetchingLead = true;
    try {
      const response = await APIClient.fetchLeadByUuid({ uuid });
      if (response.success) {
        this.leadState.lead = response.lead ?? null;
      } else {
        this.leadState.lead = null;
      }
      return response;
    } catch (error) {
      this.leadState.lead = null;
      return {
        date: new Date(),
        request: { uuid: uuid || '' },
        status: 500,
        success: false,
        lead: null,
      } as FetchLeadByUuidResponse;
    } finally {
      this.leadState.fetchingLead = false;
    }
  };

  @action
  fetchRentalById = async (id: number): Promise<FetchRentalDetailsResponse> => {
    const useMocks = AppConfig.Settings.Server.useMockData;

    try {
      if (useMocks) {
        const rental = rentalsMock.find(r => r.id === id) || null;
        return {
          success: !!rental,
          rental,
          request: { id },
          date: new Date(),
          status: rental ? 200 : 404,
        } as FetchRentalDetailsResponse;
      } else {
        const request: FetchRentalDetailsRequest = { id };
        const apiResponse = await APIClient.fetchRentalById(request);
        return apiResponse;
      }
    } catch (error) {
      const appError = error instanceof Error ? new AppError(error) : new AppError();
      return {
        success: false,
        rental: null,
        request: { id },
        date: new Date(),
        status: 500,
        error: appError,
      } as FetchRentalDetailsResponse;
    }
  };

  @action
  fetchAvailableShowingTimes = async (rentalId: number, isAgent = false): Promise<FetchShowingAvailabilityResponse> => {
    this.showingAvailabilityState.fetchingAvailability = true;

    const request: FetchShowingAvailabilityRequest = { rentalId, isAgent };

    let response: FetchShowingAvailabilityResponse;

    const useMocks = AppConfig.Settings.Server.useMockData;

    if (useMocks) {
      response = {
        request,
        date: new Date(),
        success: true,
        status: 200,
        availableTimes: availableShowingTimesMock,
      };
    } else {
      const httpResponse = await APIClient.fetchAvailableShowingTimes(request);

      response = {
        ...httpResponse,
        availableTimes: httpResponse.availableTimes || [],
      };
    }

    this.showingAvailabilityState.fetchingAvailability = false;

    if (response.success) {
      this.setAvailableShowingTimes(response.availableTimes || []);
    } else {
      this.setAvailableShowingTimes([]);
    }

    return response;
  };

  @action
  setRentals = (rentals: Rental[]) => {
    this.rentalsState.rentals = rentals;
  };

  @action
  signOutUser = async (): Promise<SignOutUserResponse> => {
    const { baseUrl } = AppConfig.Settings.Server;
    const response = await ScaffoldClient.signOutUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
    });
    return response;
  };

  @action
  setFetchingUser = (value: boolean) => {
    this.userState.fetchingUser = value;
  };

  @action
  setUser = (user?: User) => {
    this.userState.user = user;
  };

  @action
  setAvailableShowingTimes = (availability: ShowingAvailability[]) => {
    this.showingAvailabilityState.availability = availability;
  };

  @action
  setLead = (lead: Lead | null) => {
    this.leadState.lead = lead;
  };

  @action
  async scheduleShowing(request: ScheduleShowingRequest): Promise<ScheduleShowingResponse> {
    return APIClient.scheduleShowing(request);
  }

  @action
  async getAgent(request: GetAgentRequest): Promise<GetAgentResponse> {
    return APIClient.getAgent(request);
  }

  @action
  async updateOrCreateAgent(request: UpdateOrCreateAgentRequest): Promise<UpdateOrCreateAgentResponse> {
    return APIClient.updateOrCreateAgent(request);
  }

  @action
  async createLead(request: CreateLeadRequest): Promise<APIResponse> {
    return APIClient.createLead(request);
  }
}
